/**
 * Hooks React Query pour les annonces (liste, création, suppression)
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import type {
  AnnouncementQueryDto,
  PaginatedAnnouncementsResponse,
} from '@sphere/communication/domain'
import {
  createAnnouncement,
  deleteAnnouncement,
  fetchAnnouncements,
  fetchAnnouncementModuleCapabilities,
  fetchUnreadAnnouncementCount,
  markAnnouncementAsRead,
} from '../services'
import type { AnnouncementModuleCapabilities } from '@sphere/communication/domain'

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const announcementKeys = {
  all: ['announcements'] as const,
  lists: () => [...announcementKeys.all, 'list'] as const,
  list: (params: AnnouncementQueryDto) =>
    [...announcementKeys.lists(), params] as const,
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Liste paginée des annonces (filtres passés au backend)
 */
export function useAnnouncements(
  params: AnnouncementQueryDto = {},
  options?: Omit<
    UseQueryOptions<PaginatedAnnouncementsResponse>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<PaginatedAnnouncementsResponse>({
    queryKey: announcementKeys.list(params),
    queryFn: () => fetchAnnouncements(params),
    ...options,
  })
}

/**
 * Nombre d'annonces non lues (pour le badge du TopBar)
 */
export function useUnreadAnnouncementCount(
  options?: Omit<UseQueryOptions<number>, 'queryKey' | 'queryFn'>
) {
  return useQuery<number>({
    queryKey: [...announcementKeys.all, 'unread-count'] as const,
    queryFn: fetchUnreadAnnouncementCount,
    refetchInterval: 60_000,
    ...options,
  })
}

/**
 * Capabilities globales du module annonces (ex. afficher le bouton « Nouvelle annonce »)
 */
export function useAnnouncementModuleCapabilities(
  options?: Omit<
    UseQueryOptions<AnnouncementModuleCapabilities>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<AnnouncementModuleCapabilities>({
    queryKey: [...announcementKeys.all, 'capabilities'] as const,
    queryFn: fetchAnnouncementModuleCapabilities,
    ...options,
  })
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Création d'une annonce — invalide les listes au succès.
 * Utiliser le 2e argument de mutate() pour onSuccess / onError côté UI (toasts, etc.).
 */
export function useCreateAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: createAnnouncement,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.all })
    },
  })
}

/**
 * Suppression d'une annonce — invalide les listes au succès.
 */
export function useDeleteAnnouncement() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteAnnouncement,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.all })
    },
  })
}

/**
 * Marque une annonce comme lue — invalide les listes (et le compteur) au succès.
 */
export function useMarkAnnouncementAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAnnouncementAsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementKeys.all })
    },
  })
}
